import { apiService } from './ApiService';
import { TableSession } from '../models/TableSession';
import { Table } from '../models/Table';
import { User } from '../models/User';
import { modelRegistry } from '../models/Model';
import Store from '../wrappers/Store';
import { TableSessionStatusType, OrderStatusType } from '../types';
import { HashGen } from '../utilities';
const TableSessionService = {
    /** Check if table sessions are enabled (sessions OR QR ordering) */
    isSessionsEnabled() {
        const tenant = Store.get('tenant');
        const settings = tenant?.venue?.settings;
        return !!settings?.table_sessions_enabled || this.isQrOrderingEnabled();
    },
    /** Check if QR ordering is enabled for current venue */
    isQrOrderingEnabled() {
        const modules = Store.get('modules');
        const tenant = Store.get('tenant');
        return !!modules?.qr_ordering
            && !!tenant?.venue?.settings?.qr_ordering?.enabled;
    },
    /** Create an explicit session for a table (Open Session button) */
    async openSession(tableId) {
        // Check if an active session already exists locally
        const existing = TableSession.forTable(tableId);
        if (existing)
            return { session: existing };
        const tenant = Store.get('tenant');
        const user = User.current();
        const session = new TableSession({
            hash: HashGen.long(),
            table_id: tableId,
            tenant_id: tenant?.id || 0,
            pin: TableSession.generatePin(),
            status: TableSessionStatusType.active,
            opened_by: user?.id || 0,
            opened_at: new Date().toISOString(),
        });
        const result = await session.sync('create');
        if (!result.success) {
            return { session: null, error: 'error' };
        }
        return { session };
    },
    /** Close a session. Works offline — syncs when connectivity returns. */
    async closeSession(session) {
        session.status = TableSessionStatusType.closed;
        session.closed_at = new Date().toISOString();
        const result = await session.sync('update');
        return { session, error: result.success ? undefined : 'sync_pending' };
    },
    /** True when the table belongs to a beach sector — beach sessions are strictly manual (check-in / check-out) */
    isBeachTable(tableId) {
        return Table.find(tableId)?.getSector()?.type === 'beach';
    },
    /** True when the table can host sessions — regular type, non-beach sector (mirrors BE TableSessionsController rules) */
    canHostSession(tableId) {
        const table = Table.find(tableId);
        if (!table || table.type !== 'regular')
            return false;
        return table.getSector()?.type !== 'beach';
    },
    /**
     * Auto-open a session when a new open tab lands on a regular-sector
     * table (FE-driven, offline-first). Returns the opened session so the
     * caller can surface the PIN modal. No-op when a session already exists.
     */
    async handleAutoOpen(order) {
        if (order.status === OrderStatusType.unpaid
            && !order.correctsOthers()
            && order.table_id
            && this.canHostSession(order.table_id)
            && this.isQrOrderingEnabled()
            && !TableSession.forTable(order.table_id)) {
            const { session } = await this.openSession(order.table_id);
            return { openedSession: session };
        }
        return { openedSession: null };
    },
    /**
     * Auto-close sessions on tables an order event may have emptied.
     * MUST run against settled store state — callers anchor it after the
     * real sync completes (not the 2s-raced result), or after local
     * persistence on the offline path. Order.onTables() is the floor
     * plan's own source of truth, so the session closes exactly when the
     * table card empties. Beach sectors are skipped entirely.
     */
    async handleAutoClose(order, force = false) {
        // Only events that can REMOVE open tabs: payments (paid/summary),
        // corrective orders (fiscal voids), and cancellations. Anything else
        // (new tabs, proforma, advance, copies) cannot empty a table.
        // `force` covers table-emptying deletes (Order.deleteItem calls this
        // directly after $delete()) — a deleted order has no canceled_at or
        // paid status to match the standard gate.
        const couldRemoveTabs = order.correctsOthers()
            || order.canceled()
            || order.status === OrderStatusType.paid
            || order.status === OrderStatusType.summary;
        if (!force && !couldRemoveTabs)
            return;
        const tableIds = [order.table_id, ...order.paysForOrders().map(o => o.table_id)]
            .filter((id) => !!id)
            .filter((id, i, arr) => arr.indexOf(id) === i);
        if (!tableIds.length)
            return;
        const OrderClass = modelRegistry.getModelClass('Order');
        if (!OrderClass)
            return;
        const openOrders = OrderClass.onTables();
        for (const tableId of tableIds) {
            if (this.isBeachTable(tableId))
                continue;
            const session = TableSession.forTable(tableId);
            if (!session)
                continue;
            if (openOrders.some(o => o.table_id === tableId))
                continue;
            await this.closeSession(session);
        }
    },
    /**
     * Session semantics for a table transfer: destination has an active
     * session → it wins, the source session closes (merging parties);
     * otherwise the source session moves with the guests (same PIN/token).
     * Applies to beach sectors too — a transfer is a staff action.
     */
    async handleTableTransfer(sourceTableId, destinationTableId) {
        const sourceSession = TableSession.forTable(sourceTableId);
        if (!sourceSession)
            return;
        // Destination must be able to host sessions (regular type, any sector) —
        // moving to a limited table closes the session like a merge would.
        const destinationTable = Table.find(destinationTableId);
        if (!destinationTable || destinationTable.type !== 'regular') {
            await this.closeSession(sourceSession);
            return;
        }
        if (TableSession.forTable(destinationTableId)) {
            await this.closeSession(sourceSession);
            return;
        }
        sourceSession.table_id = destinationTableId;
        await sourceSession.sync('transfer');
    },
    /**
     * Staff CLAIMS a pending order request (pending → approved). The backend
     * does NOT create the order — the caller issues it through the normal
     * frontend pipeline and then links it via finalizeOrderRequest. Online-only:
     * the claim is the exactly-once gate, so it can't be deferred offline.
     */
    async approveOrderRequest(orderRequestId) {
        if (!Store.get('isOnline')) {
            return { success: false, error: 'requires_connection' };
        }
        try {
            await apiService.patch(`/order-requests/${orderRequestId}/approve`, {});
            return { success: true };
        }
        catch (e) {
            const status = e?.response?.status;
            // 404/409: already linked/rejected, or claimed by another terminal
            if (status === 404 || status === 409) {
                return { success: false, error: 'already_processed' };
            }
            return { success: false, error: e?.response?.data?.message || 'error' };
        }
    },
    /**
     * Link the FE-issued order to its approved request (order_hash is the
     * durable key, order_id backfills once the order has synced), or revert the
     * request to `pending` when the FE issue failed so it re-appears for retry.
     */
    async finalizeOrderRequest(orderRequestId, payload) {
        try {
            await apiService.patch(`/order-requests/${orderRequestId}/finalize`, payload);
            return { success: true };
        }
        catch {
            return { success: false };
        }
    },
    /** Staff rejects a pending order request */
    async rejectOrderRequest(orderRequestId, reason) {
        if (!Store.get('isOnline')) {
            return { success: false, error: 'requires_connection' };
        }
        try {
            await apiService.patch(`/order-requests/${orderRequestId}/reject`, { reason });
            return { success: true };
        }
        catch (e) {
            const status = e?.response?.status;
            // 404/409: request was already processed by another terminal
            if (status === 404 || status === 409) {
                return { success: false, error: 'already_processed' };
            }
            return { success: false, error: e?.response?.data?.message || 'error' };
        }
    },
};
export default TableSessionService;
export { TableSessionService };
//# sourceMappingURL=TableSessionService.js.map